import React from 'react';
import { ArrowRight, Truck, Award, Heart, ShoppingBag } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import OptimizedImage from '../components/OptimizedImage';

const Home = () => {
  const { dispatch } = useCart();

  const featuredMangoes = [
    {
      id: '1',
      name: 'Kesar',
      image: 'https://images.unsplash.com/photo-1553279768-865429fa0078?auto=format&fit=crop&q=80',
      price: '120',
      description: 'The queen of mangoes, known for its sweet and rich pulp'
    },
    {
      id: '2',
      name: 'Alphonso',
      image: 'https://images.unsplash.com/photo-1601493700631-2b16ec4b4716?auto=format&fit=crop&q=80',
      price: '108',
      description: 'The king of mangoes, rich and creamy'
    },
    {
      id: '3',
      name: 'Banganapalli',
      image: 'https://images.unsplash.com/photo-1519096845289-95806ee03a1a?auto=format&fit=crop&q=80',
      price: '120',
      description: 'Sweet and aromatic, perfect for milkshakes'
    }
  ];

  const handlePreOrder = (mango: any) => {
    dispatch({
      type: 'ADD_ITEM',
      payload: {
        id: mango.id,
        name: mango.name,
        price: mango.price,
        quantity: 1,
        image: mango.image
      }
    });
  };

  return (
    <div>
      {/* Hero Section */}
      <section className="relative min-h-[600px]">
        <div className="absolute inset-0">
          <OptimizedImage
            src="https://images.unsplash.com/photo-1590244843380-09f8fc7a7c4c?auto=format&fit=crop&q=80"
            alt="Mango Farm"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 to-black/50"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-20">
          <div className="max-w-2xl">
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Experience the Finest Mangoes of Telugu Land
            </h1>
            <div className="bg-black/30 backdrop-blur-sm p-6 rounded-xl mb-8">
              <p className="text-yellow-300 font-serif mb-3">
                From Nuzvid's sweet Rasalu to Krishna's tangy Totapuri,
                we bring you nature's finest treasures.
              </p>
              <p className="text-yellow-200 font-serif mb-3">
                Savor the golden Banganapalli and royal Alphonso,
                handpicked from the finest orchards.
              </p>
            </div>
            <div className="flex gap-4">
              <Link
                to="/shop"
                className="bg-yellow-500 text-white px-8 py-3 rounded-full font-semibold hover:bg-yellow-600 transition-colors inline-flex items-center"
              >
                Shop Now
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
              <Link
                to="/shop"
                className="bg-white/10 backdrop-blur-sm text-white px-8 py-3 rounded-full font-semibold hover:bg-white/20 transition-colors inline-flex items-center"
              >
                <ShoppingBag className="mr-2 h-5 w-5" />
                Pre-Order Now
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Truck className="h-8 w-8 text-yellow-500" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Quick Delivery</h3>
              <p className="text-gray-600">Fresh mangoes delivered within 24-48 hours of harvest</p>
            </div>
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Award className="h-8 w-8 text-yellow-500" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Premium Quality</h3>
              <p className="text-gray-600">Handpicked and carefully selected mangoes</p>
            </div>
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Heart className="h-8 w-8 text-yellow-500" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Customer First</h3>
              <p className="text-gray-600">100% satisfaction guaranteed</p>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-12">Featured Mangoes</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {featuredMangoes.map((mango) => (
              <div key={mango.id} className="bg-white rounded-xl shadow-md overflow-hidden transform transition-transform hover:scale-105">
                <OptimizedImage
                  src={mango.image}
                  alt={mango.name}
                  className="w-full h-48 object-cover"
                />
                <div className="p-6">
                  <h3 className="text-xl font-semibold mb-2">{mango.name}</h3>
                  <p className="text-gray-600 mb-4">{mango.description}</p>
                  <div className="flex justify-between items-center">
                    <span className="text-xl font-bold text-yellow-500">₹{mango.price}/kg</span>
                    <button
                      onClick={() => handlePreOrder(mango)}
                      className="bg-yellow-500 text-white px-4 py-2 rounded-lg hover:bg-yellow-600 transition-colors flex items-center gap-2"
                    >
                      <ShoppingBag className="h-5 w-5" />
                      Pre-Order
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;